/**
 * Challenge Options - Rich descriptions for personalized learning paths
 * Based on Gabi's feedback: Problem → Outcome → Learning Path → Quick Win
 */

export interface ChallengeOption {
  id: string;
  title: string;
  problem: string;
  outcome: string;
  learningPath: Array<{
    id: string;
    why: string;
  }>;
  quickWinId: string;
  estimatedTotalTime: number; // minutes
}

// Path A: Communication & Fundraising
export const pathAChallenges: Record<string, ChallengeOption> = {
  A1: {
    id: 'a1',
    title: "I'm drowning in writing tasks",
    problem: 'Spending 10-15 hours/week on emails, posts, reports with no time for strategic work',
    outcome: 'Cut writing time in half while improving quality and consistency',
    learningPath: [
      { id: 'communication-systems', why: 'Set up one reusable template right away to start saving time' },
      { id: 'momentum-brief', why: 'Practice briefing a busy stakeholder on your progress' },
      { id: 'spot-hallucinations', why: 'Audit your AI drafts to catch errors before they ship' },
      { id: 'ops-playbook-builder', why: 'Build a repeatable playbook for your writing workflow' },
      { id: 'voice-tune-up', why: 'Inject your unique voice into generic AI drafts' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  },
  A2: {
    id: 'a2',
    title: 'Everything I write feels rushed or generic',
    problem: 'No time to make communications feel personal or on-brand - everything sounds like everyone else',
    outcome: 'Produce authentic, compelling content consistently that sounds like your organization',
    learningPath: [
      { id: 'momentum-brief', why: 'Test if your voice lands with a skeptical audience' },
      { id: 'spot-hallucinations', why: 'Critique AI outputs to ensure they match your tone' },
      { id: 'communication-systems', why: 'Build voice templates so branding stays consistent' },
      { id: 'voice-tune-up', why: 'Practice rewriting "robot text" until it sounds like you' },
      { id: 'strategic-direction', why: 'Tie each message back to your mission' }
    ],
    quickWinId: 'momentum-brief',
    estimatedTotalTime: 35
  },
  A3: {
    id: 'a3',
    title: 'I need to turn data/reports into compelling stories',
    problem: 'Great impact data buried in boring documents - donors and stakeholders glaze over',
    outcome: 'Transform existing content into donor-ready narratives that inspire action',
    learningPath: [
      { id: 'content-intelligence', why: 'Pull compelling stories out of dry reports' },
      { id: 'momentum-brief', why: 'Practice pitching these stories to a busy donor' },
      { id: 'strategic-direction', why: 'Aim the story at your communication goals' },
      { id: 'spot-hallucinations', why: 'Fact-check the story basics so it stays credible' },
      { id: 'ops-playbook-builder', why: 'Create a process for turning data into stories' }
    ],
    quickWinId: 'content-intelligence',
    estimatedTotalTime: 35
  },
  A4: {
    id: 'a4',
    title: 'I spend all week writing instead of building relationships',
    problem: 'Administrative work crowds out strategic relationship time with donors and partners',
    outcome: 'Automate routine communications to focus on donor relationships and mission work',
    learningPath: [
      { id: 'communication-systems', why: 'Systematize your most common notes first' },
      { id: 'momentum-brief', why: 'Keep major donors updated with quick, punchy briefs' },
      { id: 'spot-hallucinations', why: 'Ensure automated notes still sound human and accurate' },
      { id: 'ops-playbook-builder', why: 'Document exactly how you nurture relationships' },
      { id: 'content-intelligence', why: 'Reuse one-to-one insights for many contacts' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  }
};

// Path B: Programs & Projects
export const pathBChallenges: Record<string, ChallengeOption> = {
  B1: {
    id: 'b1',
    title: 'Too many ideas - stuck in analysis paralysis',
    problem: 'Stuck in analysis paralysis, making gut decisions without data to back them up',
    outcome: 'Make confident, evidence-based program choices quickly with AI-powered analysis',
    learningPath: [
      { id: 'analyze-options', why: 'Let AI walk you through comparing your options' },
      { id: 'ops-playbook-builder', why: 'Turn the winning idea into a concrete plan' },
      { id: 'crisis-triage', why: 'Practice prioritizing competing demands under pressure' },
      { id: 'momentum-brief', why: 'Pitch the decision to your board or team' },
      { id: 'spot-hallucinations', why: 'Audit your analysis for blind spots' }
    ],
    quickWinId: 'analyze-options',
    estimatedTotalTime: 35
  },
  B2: {
    id: 'b2',
    title: 'Budget problems surface too late to fix',
    problem: 'Budget surprises derail programs mid-execution - no early warning system',
    outcome: 'Catch financial issues early with real-time AI-powered monitoring and alerts',
    learningPath: [
      { id: 'monitor-finances', why: 'Set up budget tracking on one current project' },
      { id: 'ops-playbook-builder', why: 'Map the exact steps for financial reviews' },
      { id: 'momentum-brief', why: 'Practice delivering bad financial news effectively' },
      { id: 'analyze-options', why: 'Compare financial trade-offs before you commit' },
      { id: 'spot-hallucinations', why: 'Verify your financial assumptions' }
    ],
    quickWinId: 'monitor-finances',
    estimatedTotalTime: 35
  },
  B3: {
    id: 'b3',
    title: "Juggling projects - losing track of details",
    problem: 'Constant fire-fighting, things falling through cracks across multiple projects',
    outcome: 'Maintain clear visibility across all projects without micromanaging your team',
    learningPath: [
      { id: 'automated-oversight', why: 'Automate status updates on your key project' },
      { id: 'momentum-brief', why: 'Get a clear signal on project health' },
      { id: 'ops-playbook-builder', why: 'Create a standard operating procedure for updates' },
      { id: 'spot-hallucinations', why: 'Check automated reports for accuracy' },
      { id: 'analyze-options', why: 'Sort what needs your attention now vs. later' }
    ],
    quickWinId: 'automated-oversight',
    estimatedTotalTime: 35
  },
  B4: {
    id: 'b4',
    title: 'Team is overwhelmed - how to rebalance?',
    problem: 'Discovering burnout too late, losing good people because workload is invisible',
    outcome: 'Protect team sustainability while maximizing impact through smart workload analysis',
    learningPath: [
      { id: 'ops-playbook-builder', why: 'Map the team\'s actual workflow to find waste' },
      { id: 'automated-oversight', why: 'Offload repetitive tasks to free capacity' },
      { id: 'grant-negotiation', why: 'Practice advocating for resources with a tough stakeholder' },
      { id: 'monitor-finances', why: 'Track workload signals to catch burnout early' },
      { id: 'spot-hallucinations', why: 'Critique the workload data for bias' }
    ],
    quickWinId: 'ops-playbook-builder',
    estimatedTotalTime: 35
  }
};

// Path C: Efficiency & Systems
export const pathCChallenges: Record<string, ChallengeOption> = {
  C1: {
    id: 'c1',
    title: "Doing the same tasks over and over",
    problem: 'Repeating identical work that should be templated - wasting hours every week',
    outcome: 'Build reusable AI-powered systems that save 10+ hours weekly on repetitive work',
    learningPath: [
      { id: 'communication-systems', why: 'Systematize your most frequent messages first' },
      { id: 'ops-playbook-builder', why: 'Turn a repeated task into a delegated process' },
      { id: 'momentum-brief', why: 'Update your boss on the time you saved' },
      { id: 'grant-negotiation', why: 'Negotiate for the tools/budget you need to automate' },
      { id: 'spot-hallucinations', why: 'Ensure your templates do not generate errors' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  },
  C2: {
    id: 'c2',
    title: 'Days disappear into admin work',
    problem: 'No time for strategic work or mission delivery - drowning in administrative tasks',
    outcome: 'Reclaim 10+ hours/week for high-impact activities by automating admin work',
    learningPath: [
      { id: 'communication-systems', why: 'Build one comms system so you save time today' },
      { id: 'ops-playbook-builder', why: 'Document the admin tasks you want to automate' },
      { id: 'spot-hallucinations', why: 'Verify that automation isn\'t making mistakes' },
      { id: 'momentum-brief', why: 'Brief the team on the new admin process' },
      { id: 'monitor-finances', why: 'Use a simple dashboard instead of manual checks' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  },
  C3: {
    id: 'c3',
    title: 'Need professional docs without spending weeks',
    problem: 'Proposals and plans take too long to create - quality suffers under time pressure',
    outcome: 'Generate investor-quality documentation in hours instead of weeks',
    learningPath: [
      { id: 'ops-playbook-builder', why: 'Create a standard doc generation workflow' },
      { id: 'strategic-direction', why: 'Align the document to your strategic goals' },
      { id: 'momentum-brief', why: 'Pitch the final document to stakeholders' },
      { id: 'analyze-options', why: 'Generate analysis sections quickly with AI' },
      { id: 'spot-hallucinations', why: 'Proofread the AI draft for hallucinations' }
    ],
    quickWinId: 'ops-playbook-builder',
    estimatedTotalTime: 35
  },
  C4: {
    id: 'c4',
    title: 'Need better data but no time to analyze',
    problem: 'Making choices without proper analysis - flying blind on important decisions',
    outcome: 'Get rapid, thorough AI-powered analysis to support smart decisions',
    learningPath: [
      { id: 'analyze-options', why: 'Analyze one current decision with AI side-by-side' },
      { id: 'momentum-brief', why: 'Present the data story to leadership' },
      { id: 'ops-playbook-builder', why: 'Build a repeatable analysis process' },
      { id: 'build-plans', why: 'Turn the analysis into a clear plan' },
      { id: 'spot-hallucinations', why: 'Check the data analysis for hallucinations' }
    ],
    quickWinId: 'analyze-options',
    estimatedTotalTime: 35
  }
};

// Path D: Assessment Route (diagnostic questions lead here)
export const pathDChallenges: Record<string, ChallengeOption> = {
  D1: {
    id: 'd1',
    title: 'Writing/communicating takes most time',
    problem: 'Assessment showed communication tasks dominate your week',
    outcome: 'Streamline writing and communication workflows with AI assistance',
    learningPath: [
      { id: 'communication-systems', why: 'Build communication templates you can reuse' },
      { id: 'momentum-brief', why: 'Practice efficient updates' },
      { id: 'spot-hallucinations', why: 'Catch errors in your AI drafts' },
      { id: 'voice-tune-up', why: 'Ensure your automated content still sounds human' },
      { id: 'ops-playbook-builder', why: 'Standardize your writing workflow' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  },
  D2: {
    id: 'd2',
    title: 'Project planning/execution takes most time',
    problem: 'Assessment showed project management dominates your workload',
    outcome: 'Make smarter project decisions and streamline execution',
    learningPath: [
      { id: 'analyze-options', why: 'Compare your options with a simple matrix' },
      { id: 'ops-playbook-builder', why: 'Map out the execution steps' },
      { id: 'crisis-triage', why: 'Learn to decide what NOT to do when overwhelmed' },
      { id: 'momentum-brief', why: 'Keep the team aligned with briefs' },
      { id: 'spot-hallucinations', why: 'Verify status reports' }
    ],
    quickWinId: 'analyze-options',
    estimatedTotalTime: 35
  },
  D3: {
    id: 'd3',
    title: 'Repetitive admin tasks consume your time',
    problem: 'Assessment revealed efficiency challenges draining your energy',
    outcome: 'Build systems and automation to reclaim time for mission work',
    learningPath: [
      { id: 'communication-systems', why: 'Build a simple comms system so work repeats less' },
      { id: 'ops-playbook-builder', why: 'Create a playbook for the admin task' },
      { id: 'spot-hallucinations', why: 'Audit the automated output' },
      { id: 'ai-briefing', why: 'Train AI to handle your routine tasks' },
      { id: 'momentum-brief', why: 'Report on efficiency wins' }
    ],
    quickWinId: 'communication-systems',
    estimatedTotalTime: 35
  },
  D4: {
    id: 'd4',
    title: 'Information overload causes stress',
    problem: 'Assessment showed decision-making under uncertainty is your key challenge',
    outcome: 'Get clarity and confidence through AI-powered analysis and insights',
    learningPath: [
      { id: 'analyze-options', why: 'Break down one complex decision with AI' },
      { id: 'momentum-brief', why: 'Summarize the situation for clarity' },
      { id: 'crisis-triage', why: 'Force yourself to rank priorities objectively' },
      { id: 'ops-playbook-builder', why: 'Build a decision-making framework' },
      { id: 'spot-hallucinations', why: 'Fact-check the information source' }
    ],
    quickWinId: 'analyze-options',
    estimatedTotalTime: 35
  }
};

// Combined export for easy lookup
export const allChallengeOptions: Record<string, ChallengeOption> = {
  ...pathAChallenges,
  ...pathBChallenges,
  ...pathCChallenges,
  ...pathDChallenges
};

// Helper function to get challenge by ID
export const getChallengeOption = (challengeId: string): ChallengeOption | undefined => {
  return allChallengeOptions[challengeId.toUpperCase()];
};

// Helper to get all challenges for a focus area
export const getChallengesForFocus = (focus: string): ChallengeOption[] => {
  switch (focus) {
    case 'Communication/Fundraising':
      return Object.values(pathAChallenges);
    case 'Programs/Projects':
      return Object.values(pathBChallenges);
    case 'Efficiency/Systems':
      return Object.values(pathCChallenges);
    case 'Assessment':
      return Object.values(pathDChallenges);
    default:
      return [];
  }
};
